import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { mcpApi } from "@/services/api-v2.service";
import { Plus, Settings, Play, Square, Trash2, TestTube } from "lucide-react";
import type { McpConnection, McpConnectionCreate, McpConnectionUpdate } from "@/types/api-v2";

// MCP Connection schema for form validation
const mcpConnectionSchema = z.object({
  name: z.string().min(1, "Название обязательно").max(100),
  url: z.string().url("Некорректный URL"),
  authType: z.enum(["none", "bearer", "basic", "api-key"]),
  authToken: z.string().optional(),
  timeout: z.number().min(5).max(300),
  retryAttempts: z.number().min(0).max(10),
  autoConnect: z.boolean(),
});

type McpConnectionForm = z.infer<typeof mcpConnectionSchema>;

export default function McpClientManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<McpConnection | null>(null);

  // Fetch MCP connections
  const { data: connections = [], isLoading } = useQuery<McpConnection[]>({
    queryKey: ["mcp", "connections"],
    queryFn: () => mcpApi.list(),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Form setup
  const form = useForm<McpConnectionForm>({
    resolver: zodResolver(mcpConnectionSchema),
    defaultValues: {
      name: "",
      url: "",
      authType: "none",
      authToken: "",
      timeout: 30,
      retryAttempts: 3,
      autoConnect: false,
    },
  });

  // Create connection mutation
  const createConnectionMutation = useMutation({
    mutationFn: async (data: McpConnectionForm) => {
      const connectionData: McpConnectionCreate = {
        name: data.name,
        url: data.url,
        authType: data.authType,
        authToken: data.authToken || null,
        timeout: data.timeout,
        retryAttempts: data.retryAttempts,
        autoConnect: data.autoConnect,
        isActive: false,
      };
      return await mcpApi.create(connectionData);
    },
    onSuccess: () => {
      toast({
        title: "Успех",
        description: "MCP соединение создано успешно",
      });
      setIsDialogOpen(false);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["mcp", "connections"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update connection mutation
  const updateConnectionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<McpConnectionForm> }) => {
      const updateData: McpConnectionUpdate = {
        name: data.name,
        url: data.url,
        authType: data.authType,
        authToken: data.authToken || null,
        timeout: data.timeout,
        retryAttempts: data.retryAttempts,
        autoConnect: data.autoConnect,
      };
      return await mcpApi.update(id, updateData);
    },
    onSuccess: () => {
      toast({
        title: "Успех",
        description: "MCP соединение обновлено успешно",
      });
      setIsDialogOpen(false);
      setEditingConnection(null);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["mcp", "connections"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete connection mutation
  const deleteConnectionMutation = useMutation({
    mutationFn: async (id: number) => {
      await mcpApi.delete(id);
    },
    onSuccess: () => {
      toast({
        title: "Успех",
        description: "MCP соединение удалено успешно",
      });
      queryClient.invalidateQueries({ queryKey: ["mcp", "connections"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Connect to MCP server mutation
  const connectMutation = useMutation({
    mutationFn: async (id: number) => {
      // Обновляем соединение, устанавливая isActive = true
      await mcpApi.update(id, { isActive: true });
      return { success: true, message: "Соединение установлено" };
    },
    onSuccess: (data) => {
      toast({
        title: data.success ? "Успех" : "Ошибка",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["mcp", "connections"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Disconnect from MCP server mutation
  const disconnectMutation = useMutation({
    mutationFn: async (id: number) => {
      // Обновляем соединение, устанавливая isActive = false
      await mcpApi.update(id, { isActive: false });
      return { success: true, message: "Соединение разорвано" };
    },
    onSuccess: (data) => {
      toast({
        title: data.success ? "Успех" : "Ошибка",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["mcp", "connections"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: async (id: number) => {
      const result = await mcpApi.test(id);
      return {
        success: result.status === "ok",
        message: result.message,
        latency: result.latencyMs,
      };
    },
    onSuccess: (data) => {
      toast({
        title: data.success ? "Тест успешен" : "Тест не пройден",
        description: data.latency ? `${data.message} (${data.latency}мс)` : data.message,
        variant: data.success ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка теста",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: McpConnectionForm) => {
    if (editingConnection) {
      updateConnectionMutation.mutate({ id: editingConnection.id, data });
    } else {
      createConnectionMutation.mutate(data);
    }
  };

  const handleEdit = (connection: McpConnection) => {
    setEditingConnection(connection);
    form.reset({
      name: connection.name,
      url: connection.url,
      authType: connection.authType as any,
      timeout: connection.timeout,
      retryAttempts: connection.retryAttempts,
      autoConnect: connection.autoConnect,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Вы уверены, что хотите удалить это соединение?")) {
      deleteConnectionMutation.mutate(id);
    }
  };

  const handleConnect = (id: number) => {
    connectMutation.mutate(id);
  };

  const handleDisconnect = (id: number) => {
    disconnectMutation.mutate(id);
  };

  const handleTest = (id: number) => {
    testConnectionMutation.mutate(id);
  };

  const resetForm = () => {
    setEditingConnection(null);
    form.reset();
    setIsDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">MCP Клиент</h2>
          <p className="text-muted-foreground">
            Управление подключениями к внешним MCP серверам
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить соединение
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingConnection ? "Редактировать соединение" : "Новое MCP соединение"}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Название соединения</FormLabel>
                      <FormControl>
                        <Input placeholder="Например: OpenAI MCP Server" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL MCP сервера</FormLabel>
                      <FormControl>
                        <Input placeholder="ws://external-server.com:3001/mcp" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="timeout"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Таймаут (сек)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} onChange={e => field.onChange(parseInt(e.target.value))} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="retryAttempts"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Попыток переподключения</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} onChange={e => field.onChange(parseInt(e.target.value))} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="authType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Тип аутентификации</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите тип" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="z-[9999]">
                          <SelectItem value="none">Без аутентификации</SelectItem>
                          <SelectItem value="bearer">Bearer Token</SelectItem>
                          <SelectItem value="basic">Basic Auth</SelectItem>
                          <SelectItem value="api-key">API Key</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch("authType") !== "none" && (
                  <FormField
                    control={form.control}
                    name="authToken"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Токен/Ключ</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Введите токен или API ключ" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="autoConnect"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          Автоматически подключаться при запуске
                        </FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Отмена
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={createConnectionMutation.isPending || updateConnectionMutation.isPending}
                  >
                    {createConnectionMutation.isPending || updateConnectionMutation.isPending 
                      ? "Сохранение..." 
                      : editingConnection ? "Обновить" : "Создать"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Connections Table */}
      <Card>
        <CardHeader>
          <CardTitle>Конфигурации MCP соединений</CardTitle>
          <CardDescription>
            Список всех настроенных подключений к внешним MCP серверам
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Загрузка соединений...</p>
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Нет настроенных MCP соединений</p>
              <p className="text-sm text-muted-foreground mt-1">
                Добавьте первое соединение с внешним MCP сервером
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Автоподключение</TableHead>
                  <TableHead>Последнее подключение</TableHead>
                  <TableHead>Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{connection.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {connection.authType !== "none" && `${connection.authType} auth`}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate" title={connection.url}>
                        {connection.url}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <div className={`h-2 w-2 rounded-full ${
                          connection.isActive ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        <span className="text-sm">
                          {connection.isActive ? 'Подключен' : 'Отключен'}
                        </span>
                      </div>
                      {connection.lastError && (
                        <div className="text-xs text-red-500 mt-1" title={connection.lastError}>
                          Ошибка: {connection.lastError.substring(0, 30)}...
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`text-sm ${connection.autoConnect ? 'text-green-600' : 'text-gray-500'}`}>
                        {connection.autoConnect ? 'Да' : 'Нет'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {connection.lastConnected 
                          ? new Date(connection.lastConnected).toLocaleString('ru-RU')
                          : 'Никогда'
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTest(connection.id)}
                          disabled={testConnectionMutation.isPending}
                          title="Тест соединения"
                        >
                          <TestTube className="h-3 w-3" />
                        </Button>
                        
                        {connection.isActive ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDisconnect(connection.id)}
                            disabled={disconnectMutation.isPending}
                            title="Отключить"
                          >
                            <Square className="h-3 w-3" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConnect(connection.id)}
                            disabled={connectMutation.isPending}
                            title="Подключить"
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(connection)}
                          title="Настройки"
                        >
                          <Settings className="h-3 w-3" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(connection.id)}
                          disabled={deleteConnectionMutation.isPending || connection.isActive}
                          title="Удалить"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Statistics Card */}
      {connections.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Статистика подключений</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{connections.length}</div>
                <div className="text-sm text-muted-foreground">Всего соединений</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {connections.filter(c => c.isActive).length}
                </div>
                <div className="text-sm text-muted-foreground">Активных</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {connections.filter(c => c.autoConnect).length}
                </div>
                <div className="text-sm text-muted-foreground">Автоподключение</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {connections.filter(c => c.lastError).length}
                </div>
                <div className="text-sm text-muted-foreground">С ошибками</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}